import { ApprovalableTypeEnum, ApprovalRefundEnum } from 'api-hooks/common';
import {
  getRefundKey,
  getRefundsKey,
  RefundModel,
  RefundMutationInput,
  useCancelRefund,
} from 'api-hooks/refund/refund';
import { AuthorizationRules } from 'common/constants';
import { ReleaseStatusEnum } from 'common/constants/enum';
import formSetErrors from 'common/helpers/form-setError';
import { queryClient } from 'common/repositories/query-client';
import notification from 'components/common/notification';
import { Form } from 'components/elements';
import { FormState } from 'components/elements/form';
import FormHeader from 'components/widgets/form-header';
import useApprovalAction from 'hooks/use-approval-action';
import { useAuthorization } from 'hooks/use-authorization';
import useDialog from 'hooks/use-dialog';
import useResolveApproval from 'hooks/use-resolve-approval';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import RequestApprovalModal from 'legacy-modules/approval/approval/components/request-modal';
import TitleStatus from 'legacy-modules/common/title-status';
import useRefundStatus from 'legacy-modules/refund/hooks/use-refund-status';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { useRefundFormState } from '../../../hooks/use-refund-formstate';
import InformationField from '../information-field';

interface Props {
  refund?: RefundModel;
  approvalRefundType?: ApprovalRefundEnum;
  onSubmit: (
    input: RefundMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<RefundModel | undefined>;
}

export default function RefundInformationForm(props: Props) {
  const { refund, approvalRefundType } = props;
  const { t } = useTranslation();
  const dialog = useDialog();
  const { mutateAsync: cancelMutate, isLoading: cancelLoading } =
    useCancelRefund();

  const defaultValues = React.useMemo(
    () => ({
      number: refund?.number ?? '',
      requestedAt: refund?.requestedAt ?? new Date(),
      reason: refund?.reason ?? '',
    }),
    [refund?.number, refund?.reason, refund?.requestedAt],
  );

  const yupSchema = React.useMemo(
    () =>
      Yup.object().shape({
        reason: Yup.string().required(),
        requestedAt: Yup.date().required(),
      }),
    [],
  );

  const resolver = useYupValidationResolver(yupSchema);

  const methods = useForm({
    defaultValues,
    resolver,
    mode: 'all',
  });

  const onSubmit = React.useCallback(
    async (values) => {
      try {
        await props.onSubmit(values, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const onCancel = React.useCallback(async () => {
    dialog.showCustom({
      render: (onClose) => (
        <RequestApprovalModal
          onClose={onClose}
          data={{
            approvalableId: refund!.id!,
            approvalableType: ApprovalableTypeEnum.Refund,
          }}
          onSubmit={async ({ createApproval, form, input }) => {
            try {
              await cancelMutate(
                {
                  id: refund!.id!,
                },
                {
                  onSuccess: async (data) => {
                    const result = await createApproval(input);
                    notification.success({
                      message: t('common:successfully_canceled'),
                    });
                    notification.success({ message: result.message });

                    queryClient.refetchQueries([getRefundsKey()[0]]);
                    queryClient.refetchQueries(
                      getRefundKey({ id: refund!.id }),
                    );
                    onClose();
                  },
                },
              );
            } catch (error) {
              notification.error({ message: error.message });
            }
          }}
        />
      ),
    });
  }, [refund, cancelMutate, dialog, t]);

  const {
    editableAgent,
    editableCustomer,
    editableInformation,
    setEditableForm,
  } = useRefundFormState();

  const { refundStatus } = useRefundStatus({
    status: refund!.status,
  });

  const { can } = useAuthorization();

  const isPendingRelease =
    refund?.releaseSale.status === ReleaseStatusEnum.PendingReleaseApproval ||
    refund?.releasePurchase?.status ===
      ReleaseStatusEnum.PendingReleaseApproval;

  const noEdit =
    !refundStatus.isProceed ||
    !can(AuthorizationRules.RefundsUpdate) ||
    approvalRefundType === ApprovalRefundEnum.Information ||
    editableCustomer ||
    editableAgent;

  const noDelete =
    !refundStatus.isProceed ||
    !can(AuthorizationRules.RefundsCancel) ||
    editableInformation ||
    editableAgent ||
    editableCustomer ||
    approvalRefundType === ApprovalRefundEnum.Information ||
    isPendingRelease;

  const onChangeEditable = React.useCallback(
    ({ editable }: { editable: boolean }) => {
      if (editable) {
        setEditableForm({
          information: editable,
          agent: false,
          customer: false,
        });
      } else {
        setEditableForm({
          information: editable,
          agent: editableAgent,
          customer: editableCustomer,
        });
      }
    },
    [editableAgent, editableCustomer, setEditableForm],
  );

  const customViewName =
    refund && [t('common:refund'), refund.number].join(' - ');

  const { requestApprovalButton } = useApprovalAction('refunds', refund);
  const { resolverButton } = useResolveApproval('refunds', refund);

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={editableInformation}
      customViewName={customViewName}
      noReset
    >
      <FormState>
        {({ editable }) => {
          onChangeEditable({ editable });
          return (
            <FormHeader
              data={refund}
              onDelete={onCancel}
              deleteText={t('common:cancel')}
              title={
                refund ? (
                  <TitleStatus
                    title={customViewName ?? `${t('common:refund')}`}
                    status={refund.status}
                    type="refund"
                  />
                ) : (
                  t('common:add_extra', { extra: t('common:refund') })
                )
              }
              isDeleteLoading={cancelLoading}
              noEdit={noEdit}
              noDelete={noDelete}
              onRenderRightComponent={
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  {resolverButton}
                  {requestApprovalButton}
                </div>
              }
            />
          );
        }}
      </FormState>
      <div>
        <InformationField />
      </div>
    </Form>
  );
}
